export const REQUEST_RETRY_ATTEMPTS = 3;
export const REQUEST_RETRY_DELAYS_MS = [1000, 2000];
export const REQUEST_TIMEOUT_MS = 20000;

export class RegistrationLoadRetryExhaustedError extends Error {
  constructor(cause) {
    super("Registration API retries exhausted");
    this.name = "RegistrationLoadRetryExhaustedError";
    this.cause = cause;
  }
}

export const isRegistrationLoadRetryExhaustedError = (error) =>
  error?.name === "RegistrationLoadRetryExhaustedError";

export const isRequestCancelledError = (error) => {
  if (!error) {
    return false;
  }
  if (error.code === "ERR_CANCELED" || error.name === "CanceledError") {
    return true;
  }
  return error.name === "AbortError";
};

export const isRetryableRequestError = (error) => {
  if (!error || isRequestCancelledError(error)) {
    return false;
  }

  if (error.code === "ECONNABORTED") {
    return true;
  }

  if (!error.response) {
    return true;
  }

  const status = Number(error.response.status);
  return status >= 400 && status < 600;
};

const wait = (ms, signal) =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(signal.reason ?? new DOMException("Aborted", "AbortError"));
      return;
    }

    const timeoutId = window.setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);

    const onAbort = () => {
      window.clearTimeout(timeoutId);
      reject(signal.reason ?? new DOMException("Aborted", "AbortError"));
    };

    signal?.addEventListener("abort", onAbort, { once: true });
  });

/**
 * Runs an axios request up to 3 times (1s, then 2s between retries) with a 20s
 * per-attempt timeout. Successful HTTP responses are never retried, even when empty.
 */
export const executeWithRetry = async (requestFactory, { signal } = {}) => {
  let lastError = null;

  for (let attempt = 0; attempt < REQUEST_RETRY_ATTEMPTS; attempt += 1) {
    if (signal?.aborted) {
      throw signal.reason ?? new DOMException("Aborted", "AbortError");
    }

    try {
      return await requestFactory({
        signal,
        timeout: REQUEST_TIMEOUT_MS,
      });
    } catch (error) {
      lastError = error;

      if (isRequestCancelledError(error) || !isRetryableRequestError(error)) {
        throw error;
      }

      const hasAnotherAttempt = attempt < REQUEST_RETRY_ATTEMPTS - 1;
      if (!hasAnotherAttempt) {
        break;
      }

      await wait(REQUEST_RETRY_DELAYS_MS[attempt], signal);
    }
  }

  throw new RegistrationLoadRetryExhaustedError(lastError);
};
